/**
 * IhtMitigationPage — IHT Mitigation Scenario Builder
 *
 * Live-updating sliders for each mitigation lever (SIPP spenddown, lifetime
 * gifts, £3k allowance, AIM BR, charity %, whole-of-life cover) with a
 * side-by-side comparison of baseline vs scenario IHT bills both today and
 * from April 2027 (when SIPPs fall inside the estate).
 */
import { useEffect, useState } from 'react'
import {
  Shield, TrendingDown, Sparkles, RefreshCw, AlertTriangle, ArrowRight,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'
import TaxWorkspaceTabs from '@/components/ui/TaxWorkspaceTabs'

// ── Types ────────────────────────────────────────────────────────────────────
interface MitigationActions {
  sipp_spenddown_gbp_yr: number
  sipp_spenddown_years: number
  annual_gifts_gbp: number
  gift_years: number
  annual_3k_allowance: boolean
  aim_br_portfolio_gbp: number
  whole_of_life_sum_assured_gbp: number
  whole_of_life_annual_premium_gbp: number
  life_expectancy_years: number
}

interface MitigationRequest {
  spouse: boolean
  children: number
  charity_pct: number
  actions: MitigationActions
}

interface ScenarioBlock {
  iht_today_gbp: number
  iht_2027_gbp: number
  cliff_edge_gbp: number
  estate_today_gbp?: number
  estate_2027_gbp?: number
  tracked_gbp?: number
  pension_gbp?: number
  property_gross_gbp?: number
  sipp_withdrawn_gbp?: number
  gifts_fully_exempt_gbp?: number
  allowance_total_gbp?: number
  aim_br_used_gbp?: number
  charity_pct?: number
}

interface ActionRow {
  action: string
  saved_today_gbp: number | null
  saved_2027_gbp: number | null
  detail: string
  cost_gbp: number | null
  net_benefit_gbp?: number
}

interface Recommendation {
  priority: number
  action: string
  saved_2027_gbp: number | null
  cost_gbp: number | null
  detail: string
  rationale: string
}

interface MitigationResponse {
  headline: string
  baseline: ScenarioBlock
  scenario: ScenarioBlock
  savings: {
    today_gbp: number
    '2027_gbp': number
    total_over_lifetime_gbp: number
    pct_of_2027_bill: number
  }
  action_breakdown: ActionRow[]
  recommendations: Recommendation[]
  assumptions: Record<string, any>
}

// ── Formatters ───────────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtSigned = (n: number | null | undefined) => {
  if (n == null) return '—'
  if (n === 0) return '£0'
  const sign = n > 0 ? '−' : '+'
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

const fmtCompact = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`
  if (Math.abs(n) >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function IhtMitigationPage() {
  // Household
  const [spouse, setSpouse] = useState(true)
  const [children, setChildren] = useState(2)
  const [charityPct, setCharityPct] = useState(0)

  // Actions
  const [sippWdYr, setSippWdYr] = useState(0)
  const [sippYrs, setSippYrs] = useState(0)
  const [giftsYr, setGiftsYr] = useState(0)
  const [giftYrs, setGiftYrs] = useState(0)
  const [useAllowance, setUseAllowance] = useState(true)
  const [aimBr, setAimBr] = useState(0)
  const [wolSum, setWolSum] = useState(0)
  const [wolPremium, setWolPremium] = useState(0)
  const [lifeExpYrs, setLifeExpYrs] = useState(40)

  const [data, setData] = useState<MitigationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchScenario = async () => {
    setLoading(true)
    setError(null)
    try {
      const body: MitigationRequest = {
        spouse,
        children,
        charity_pct: charityPct,
        actions: {
          sipp_spenddown_gbp_yr: sippWdYr,
          sipp_spenddown_years: sippYrs,
          annual_gifts_gbp: giftsYr,
          gift_years: giftYrs,
          annual_3k_allowance: useAllowance,
          aim_br_portfolio_gbp: aimBr,
          whole_of_life_sum_assured_gbp: wolSum,
          whole_of_life_annual_premium_gbp: wolPremium,
          life_expectancy_years: lifeExpYrs,
        },
      }
      const res = await api.post<MitigationResponse>('/api/iht/mitigation/simulate', body)
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to run mitigation scenario')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchScenario()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(fetchScenario, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spouse, children, charityPct,
    sippWdYr, sippYrs, giftsYr, giftYrs, useAllowance, aimBr,
    wolSum, wolPremium, lifeExpYrs,
  ])

  const savings2027 = data?.savings?.['2027_gbp'] ?? 0
  const savingsToday = data?.savings?.today_gbp ?? 0

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield size={20} className="text-blue-light" aria-hidden />
            IHT Mitigation Planner
          </h1>
          <p className="text-sm text-text-secondary">
            Model concrete mitigation actions against your real estate — see
            what each lever does to the April 2027 cliff edge.
          </p>
        </div>
        <button type="button" onClick={fetchScenario} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      <TaxWorkspaceTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/10 text-sm text-rose-light">
          {error}
        </div>
      )}

      {/* Headline */}
      {data && (
        <div className={clsx(
          'card p-6 border-2',
          savings2027 > 0
            ? 'border-emerald/40 bg-emerald/5'
            : 'border-border bg-surface-card',
        )}>
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="panel-title opacity-80">
                Scenario summary
              </p>
              <h2 className="text-lg font-bold text-text-primary mt-1">{data.headline}</h2>
            </div>
            <div className="text-center shrink-0">
              <div className={clsx(
                'stat-hero',
                savings2027 > 0 ? 'text-emerald' : 'text-text-muted',
              )}>
                {fmtCompact(savings2027)}
              </div>
              <p className="text-xs text-text-muted mt-1">
                saved from 2027 bill
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Before / After comparison */}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* Baseline */}
          <div className="card p-5 border-rose/20 bg-rose/5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={14} className="text-rose-light" aria-hidden />
              <h3 className="panel-title">Baseline — do nothing</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted">IHT bill today</p>
                <p className="text-2xl font-bold text-text-primary">{fmt(data.baseline.iht_today_gbp)}</p>
                <p className="text-[10px] text-text-muted">SIPP outside the estate</p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">IHT bill from April 2027</p>
                <p className="text-2xl font-bold text-rose-light">{fmt(data.baseline.iht_2027_gbp)}</p>
                <p className="text-[10px] text-text-muted">SIPP inside the estate</p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">Cliff edge</p>
                <p className="text-lg font-bold text-amber">+{fmt(data.baseline.cliff_edge_gbp)}</p>
              </div>
            </div>
          </div>

          {/* Scenario */}
          <div className="card p-5 border-emerald/30 bg-emerald/5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown size={14} className="text-emerald" aria-hidden />
              <h3 className="panel-title">With your plan</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-text-muted">IHT bill today</p>
                <p className="text-2xl font-bold text-text-primary">
                  {fmt(data.scenario.iht_today_gbp)}
                </p>
                <p className="text-[10px] text-emerald">
                  {savingsToday > 0 ? `Saves ${fmt(savingsToday)}` : 'No change'}
                </p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">IHT bill from April 2027</p>
                <p className="text-2xl font-bold text-emerald-light">
                  {fmt(data.scenario.iht_2027_gbp)}
                </p>
                <p className="text-[10px] text-emerald">
                  {savings2027 > 0 ? `Saves ${fmt(savings2027)} (${data.savings.pct_of_2027_bill}%)` : 'No change'}
                </p>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">Cliff edge</p>
                <p className="text-lg font-bold text-text-primary">+{fmt(data.scenario.cliff_edge_gbp)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Mitigation levers
        </h2>

        {/* Household */}
        <div className="grid sm:grid-cols-3 gap-4 mb-5 pb-5 border-b border-border">
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={spouse}
              onChange={(e) => setSpouse(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Married (transferable NRB/RNRB)
          </label>
          <NumberSlider
            label="Children / direct descendants"
            value={children}
            onChange={setChildren}
            min={0} max={10} step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Charitable bequest %"
            value={charityPct}
            onChange={setCharityPct}
            min={0} max={50} step={1}
            format={(v) => `${v.toFixed(0)}%`}
          />
        </div>

        {/* Actions */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <NumberSlider
            label="SIPP withdrawal £/yr (pre-2027)"
            value={sippWdYr}
            onChange={setSippWdYr}
            min={0} max={200_000} step={5_000}
            format={fmt}
          />
          <NumberSlider
            label="SIPP spend-down years"
            value={sippYrs}
            onChange={setSippYrs}
            min={0} max={10} step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="AIM BR portfolio £"
            value={aimBr}
            onChange={setAimBr}
            min={0} max={500_000} step={10_000}
            format={fmt}
          />
          <NumberSlider
            label="Lifetime gifts £/yr"
            value={giftsYr}
            onChange={setGiftsYr}
            min={0} max={100_000} step={1_000}
            format={fmt}
          />
          <NumberSlider
            label="Gift years (7+ for full exemption)"
            value={giftYrs}
            onChange={setGiftYrs}
            min={0} max={20} step={1}
            format={(v) => `${v}`}
          />
          <label className="flex items-center gap-2 text-xs text-text-secondary self-end">
            <input
              type="checkbox"
              checked={useAllowance}
              onChange={(e) => setUseAllowance(e.target.checked)}
              className="rounded border-border bg-surface-card"
            />
            Use £3k annual gift allowance
          </label>
          <NumberSlider
            label="Whole-of-life sum assured £"
            value={wolSum}
            onChange={setWolSum}
            min={0} max={2_500_000} step={50_000}
            format={fmt}
          />
          <NumberSlider
            label="WOL annual premium £"
            value={wolPremium}
            onChange={setWolPremium}
            min={0} max={50_000} step={500}
            format={fmt}
          />
          <NumberSlider
            label="Life expectancy (years)"
            value={lifeExpYrs}
            onChange={setLifeExpYrs}
            min={1} max={60} step={1}
            format={(v) => `${v}`}
          />
        </div>
      </div>

      {/* Action breakdown */}
      {data && data.action_breakdown.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">Per-action breakdown</h2>
          <p className="text-xs text-text-muted mb-4">
            Each lever shown in isolation — see which one moves the needle most.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-pro">
              <thead>
                <tr>
                  <th>Action</th>
                  <th className="!text-right">Saved (today)</th>
                  <th className="!text-right">Saved (2027)</th>
                  <th className="!text-right">Cost</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.action_breakdown.map((row, i) => (
                  <tr key={i}>
                    <td className="font-semibold text-text-primary">{row.action}</td>
                    <td className="text-right tabular-nums">
                      {row.saved_today_gbp != null && row.saved_today_gbp > 0
                        ? <span className="delta-chip-pos">{fmt(row.saved_today_gbp)}</span>
                        : '—'}
                    </td>
                    <td className="text-right tabular-nums">
                      {row.saved_2027_gbp != null && row.saved_2027_gbp > 0
                        ? <span className="delta-chip-pos">{fmt(row.saved_2027_gbp)}</span>
                        : '—'}
                    </td>
                    <td className="text-right text-text-secondary tabular-nums">
                      {row.cost_gbp == null ? '—' : fmt(row.cost_gbp)}
                    </td>
                    <td className="text-text-muted">{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {data && data.recommendations.length > 0 && (
        <div className="card p-5">
          <h2 className="panel-title mb-3">
            Priority order (£-saved per £-spent)
          </h2>
          <ol className="space-y-3">
            {data.recommendations.map((rec) => (
              <li key={rec.priority} className="flex items-start gap-3 p-3 rounded-lg bg-surface-hover border border-border">
                <div className="w-7 h-7 rounded-full bg-blue/20 text-blue-light flex items-center justify-center font-bold text-xs shrink-0">
                  {rec.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-text-primary">{rec.action}</p>
                    {rec.saved_2027_gbp != null && rec.saved_2027_gbp > 0 && (
                      <span className="badge badge-green text-[10px]">
                        saves {fmt(rec.saved_2027_gbp)}
                      </span>
                    )}
                    {rec.cost_gbp != null && rec.cost_gbp > 0 && (
                      <span className="badge badge-amber text-[10px]">
                        cost {fmt(rec.cost_gbp)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-muted mt-1">{rec.detail}</p>
                  <p className="text-[10px] text-text-muted mt-1 italic flex items-center gap-1">
                    <ArrowRight size={10} aria-hidden />
                    {rec.rationale}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; assumptions
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-primary font-semibold">Baseline</span> reads your
            real tracked broker accounts + seeded property/mortgages, and computes IHT
            both today (SIPP outside the estate) and from April 2027 (SIPP inside).
          </p>
          <p>
            <span className="text-text-primary font-semibold">SIPP spend-down</span>
            &nbsp;models money as consumed (leaves the estate entirely). Income tax on
            the withdrawal is NOT modelled — net benefit shown is pre-income-tax.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Lifetime gifts</span> are
            only fully exempt once outside the 7-year window. Gifts inside the window
            are conservatively treated as still inside the estate (PET risk).
          </p>
          <p>
            <span className="text-text-primary font-semibold">AIM Business Relief</span>
            &nbsp;requires 2-year qualifying holding period and active management for
            continued qualification. Higher volatility than mainline equities.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Charity reduced rate</span>
            &nbsp;applies when ≥10% of net estate passes to charity, dropping the IHT
            rate on the remainder from 40% to 36%.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Whole-of-life</span>&nbsp;
            provides liquidity, not a bill reduction — the policy pays out tax-free (in
            trust) so heirs don't fire-sell assets. Net benefit = sum assured − premiums.
          </p>
          <p className="pt-2 text-text-muted">
            This is an estimate only. Always consult a qualified tax advisor before
            acting on any of these mitigation strategies.
          </p>
        </div>
      </details>
    </div>
  )
}

// ── Slider input ────────────────────────────────────────────────────────────
function NumberSlider({
  label, value, onChange, min, max, step, format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}
